<template>
  <div v-if="step === 2" class="fu-drw-done" role="status" data-test="fu-drawer-wizard-done">
    <fu-icon name="check" :size="15" />
    <span>Moved to {{ movedLabel || 'today' }} — now log how you followed up.</span>
  </div>
  <div v-else class="fu-drw" data-test="fu-drawer-wizard">
    <ol class="fu-drw__steps" aria-label="Steps">
      <li class="fu-drw__step fu-drw__step--on" aria-current="step"><span class="fu-drw__num">1</span>Change date</li>
      <li class="fu-drw__bar" aria-hidden="true"></li>
      <li class="fu-drw__step"><span class="fu-drw__num">2</span>Follow up</li>
    </ol>
    <div class="fu-drw__title">Follow-up is scheduled for {{ scheduledLabel || 'a later date' }}</div>
    <p class="fu-drw__sub">To follow up early, move the date to today or an earlier day.</p>
    <div class="fu-drw__row">
      <button type="button" class="fu-drw__today" :disabled="busy" data-test="fu-drawer-wizard-today" @click="$emit('move-today')">
        <fu-icon :name="busy ? 'spin' : 'cal'" :size="15" :class="{ 'fu-drw__spin': busy }" />Move to today
      </button>
      <span class="fu-drw__or">or</span>
      <label class="fu-drw__date-label">
        <span class="fu-drw__sr">Pick an earlier date</span>
        <input
          type="date"
          class="fu-drw__date"
          :max="today"
          :value="picked"
          :disabled="busy"
          :aria-invalid="pickError ? 'true' : 'false'"
          data-test="fu-drawer-wizard-date"
          @change="onPick($event.target.value)"
        >
      </label>
    </div>
    <p v-if="pickError" class="fu-drw__error" role="alert" data-test="fu-drawer-wizard-error">{{ pickError }}</p>
  </div>
</template>

<script>
/**
 * DrawerEarlyWizard — the early follow-up wizard (today's CreateFollowup.vue:319–412, in the
 * design's look): step 1 "Move to today" or a date that is today or earlier
 * (drawerAlerts.wizardDateAllowed); step 2 the design's "Moved to … — now log how you followed up."
 * line (the methods below are the next step; today's step-2 buttons are the method picker).
 *
 *   step           1 | 2 (resolveDrawerAlert's earlyWizard.step)
 *   scheduledLabel display string of the task's current follow-up date
 *   movedLabel     display string of the new date (step 2)
 *   today          'YYYY-MM-DD' — the input's max and the rule's bound
 *   busy           the date change is saving
 * Events: move-today, pick-date('YYYY-MM-DD' — only a date the rule allows). Presentational.
 */
import FuIcon from '../FuIcon.vue';
import { wizardDateAllowed } from '@/utils/followups/drawer/drawerAlerts.js';

export default {
  name: 'DrawerEarlyWizard',
  components: { FuIcon },
  props: {
    step: { type: Number, default: 1 },
    scheduledLabel: { type: String, default: '' },
    movedLabel: { type: String, default: '' },
    today: { type: String, required: true },
    busy: { type: Boolean, default: false },
  },
  data() {
    return { picked: '', pickError: '' };
  },
  methods: {
    onPick(value) {
      this.picked = value;
      if (!value) { this.pickError = ''; return; }
      if (!wizardDateAllowed(value, this.today)) {
        this.pickError = 'Pick today or an earlier day.';
        return;
      }
      this.pickError = '';
      this.$emit('pick-date', value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drw {
  padding: 14px 16px;
  border: 1px solid $fu-border;
  border-radius: 12px;
  background: $fu-neutral-25;
}

.fu-drw__steps {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  font-weight: $fu-fw-bold;
}

.fu-drw__step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $fu-text-subtle;
}

.fu-drw__step--on { color: $fu-brand-700; }

.fu-drw__num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  border: 1.5px solid $fu-neutral-300;
}

.fu-drw__step--on .fu-drw__num {
  border-color: $fu-brand;
  background: $fu-brand;
  color: #ffffff;
}

.fu-drw__bar {
  width: 28px;
  height: 2px;
  border-radius: 2px;
  background: $fu-neutral-200;
}

.fu-drw__title { margin-top: 12px; font-size: 14px; font-weight: $fu-fw-bold; }
.fu-drw__sub { margin: 3px 0 0; font-size: 13px; line-height: 1.45; color: $fu-text-muted; }

.fu-drw__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.fu-drw__today { @include fu-btn(primary, sm); }
.fu-drw__spin { @include fu-spin; }
.fu-drw__or { font-size: 12.5px; color: $fu-text-subtle; }
.fu-drw__sr { @include fu-visually-hidden; }

.fu-drw__date {
  @include fu-input(34px);
  width: auto;
  min-width: 160px;
  font-size: 13px;
  @include fu-phone { font-size: 16px; }
}

.fu-dr--phone .fu-drw__date { @include fu-dr-phone-input; }

.fu-drw__error { margin: 8px 0 0; font-size: 12.5px; font-weight: $fu-fw-semibold; color: $fu-danger-text; }

.fu-drw-done {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  color: $fu-success;
}
</style>
